import React, { useState } from 'react';
import {
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  SearchX,
  XOctagon,
  ChevronRight,
  Clock,
  Copy,
} from 'lucide-react';
import Button from '../ui/Button';
import { formatDuration, estimateRemainingMs } from '../../hooks/useErpSync';

const num = (n) => (n ?? 0).toLocaleString();

/**
 * One clickable stat row. Rows backed by a vendor list expand in place; plain rows
 * (Scanned / Assigned) just display.
 */
const StatRow = ({ icon: Icon, label, value, tone, items, expanded, onToggle }) => {
  const clickable = Array.isArray(items) && items.length > 0;
  const tones = {
    neutral: 'text-slate-600',
    good: 'text-green-700',
    warn: 'text-amber-700',
    bad: 'text-red-700',
  };
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        disabled={!clickable}
        onClick={onToggle}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left ${
          clickable ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'
        }`}
      >
        <Icon className={`h-4 w-4 shrink-0 ${tones[tone] || tones.neutral}`} />
        <span className="text-sm text-slate-700 flex-1 min-w-0 truncate">{label}</span>
        <span className={`text-sm font-semibold tabular-nums ${tones[tone] || tones.neutral}`}>
          {num(value)}
        </span>
        {clickable && (
          <ChevronRight
            className={`h-3.5 w-3.5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        )}
      </button>

      {clickable && expanded && (
        <ul className="bg-slate-50 border-t border-slate-100 max-h-64 overflow-y-auto divide-y divide-slate-100">
          {items.map((item) => (
            <li key={`${item.vendorId}-${item.companyName}`} className="px-3 py-2">
              <p className="text-xs font-medium text-slate-800 break-words">
                {item.companyName || `Vendor #${item.vendorId}`}
              </p>
              {item.reason && (
                <p className="text-[11px] text-slate-500 mt-0.5 break-words">{item.reason}</p>
              )}
              {item.candidateCount != null && (
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {item.candidateCount} possible codes — needs a decision
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * ERP sync control + outcome card.
 *
 * While a run is in flight it shows a live elapsed clock and an ETA derived from the
 * observed scan rate. Once finished it shows the run summary, where the Conflicts,
 * Unmatched and Errors rows expand into the actual vendors behind each number —
 * previously those were bare counts with no way to find out who they were.
 */
const SyncStatusPanel = ({ sync, onStart }) => {
  const [expanded, setExpanded] = useState(null);
  const { running, starting, elapsedMs, progress, lastResult } = sync;

  const scanned = progress?.scanned ?? 0;
  const total = progress?.total ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((scanned / total) * 100)) : 0;
  const etaMs = estimateRemainingMs(elapsedMs, scanned, total);
  const outcomes = lastResult?.outcomes;
  const toggle = (key) => setExpanded((cur) => (cur === key ? null : key));

  return (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b bg-slate-50">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-semibold text-slate-900 flex items-center gap-1.5">
            <RefreshCw className={`h-4 w-4 text-amber-600 ${running ? 'animate-spin' : ''}`} />
            ERP Sync
          </h2>
        </div>
        <Button
          variant="outline"
          onClick={onStart}
          loading={starting}
          disabled={running}
          className="w-full mt-3 justify-center"
          title="Sync ERP Codes"
        >
          {running ? 'Sync running…' : 'Sync ERP Codes'}
        </Button>
      </div>

      {running ? (
        <div className="px-4 py-3">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-sm font-medium text-blue-800">
              {num(scanned)} / {total ? num(total) : '…'} scanned
            </span>
            <span className="text-sm font-semibold text-blue-800 tabular-nums">{pct}%</span>
          </div>
          <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500">
            <Clock className="h-3.5 w-3.5" />
            <span className="tabular-nums">{formatDuration(elapsedMs)} elapsed</span>
            {etaMs != null && <span className="tabular-nums">· ~{formatDuration(etaMs)} left</span>}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            Runs on the server — you can leave this page and come back.
          </p>
        </div>
      ) : !lastResult ? (
        <p className="px-4 py-6 text-sm text-slate-500 text-center">
          No sync has run yet. Results will appear here.
        </p>
      ) : (
        <>
          <div className="px-4 py-2.5 border-b bg-white">
            <p className="text-xs text-slate-500">
              Last run {new Date(lastResult.finishedAt).toLocaleString()}
              {' · '}
              {lastResult.trigger === 'cron' ? 'automatic' : 'manual'}
              {lastResult.durationMs != null && ` · took ${formatDuration(lastResult.durationMs)}`}
            </p>
          </div>

          <StatRow icon={SearchX} label="Scanned" value={lastResult.fetched} tone="neutral" />
          <StatRow icon={CheckCircle2} label="Assigned" value={lastResult.assignedCount} tone="good" />
          <StatRow
            icon={AlertTriangle}
            label="Conflicts flagged"
            value={lastResult.conflictCount}
            tone="warn"
            items={outcomes?.conflicted}
            expanded={expanded === 'conflicted'}
            onToggle={() => toggle('conflicted')}
          />
          {/* Duplicates sit with the conflicts, not the errors: the sync did the right
              thing by refusing to give one unique code to two rows. They need a data
              cleanup, not a retry. */}
          <StatRow
            icon={Copy}
            label="Duplicate vendors"
            value={lastResult.duplicateCount ?? 0}
            tone="warn"
            items={outcomes?.duplicates}
            expanded={expanded === 'duplicates'}
            onToggle={() => toggle('duplicates')}
          />
          <StatRow
            icon={SearchX}
            label="Unmatched"
            value={lastResult.unmatched}
            tone="neutral"
            items={outcomes?.unmatched}
            expanded={expanded === 'unmatched'}
            onToggle={() => toggle('unmatched')}
          />
          <StatRow
            icon={XOctagon}
            label="Errors"
            value={lastResult.errors}
            tone={lastResult.errors > 0 ? 'bad' : 'neutral'}
            items={outcomes?.errored}
            expanded={expanded === 'errored'}
            onToggle={() => toggle('errored')}
          />

          {lastResult.duplicateCount > 0 && (
            <p className="px-3 py-2 text-[11px] text-slate-500 border-t bg-amber-50">
              Duplicate rows are the same company registered more than once. Only one row can
              hold a vendor code, so the extras are skipped every sync until they are merged
              or removed.
            </p>
          )}
          {outcomes?.truncated && (
            <p className="px-3 py-2 text-[11px] text-slate-400 border-t">
              Long lists are capped at 500 entries.
            </p>
          )}
          {!outcomes && (
            <p className="px-3 py-2 text-[11px] text-slate-400 border-t">
              Per-vendor detail is recorded from the next sync onwards.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default SyncStatusPanel;
